/**
 * useProductReviews Hook
 *
 * Product review'larını fetch eden ve rating hesaplayan custom hook
 * Next.js endpoint → Laravel endpoint → Redis cache
 * Onaydan önce görünmez (status='approved')
 *
 * Lazy load: Sayfa tam yüklendikten sonra, yorumlar alanı viewport'a girdiğinde fetch eder.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { clientFetchDedupe } from '@/lib/client-fetch-dedupe';

export interface Review {
    id: number;
    name: string;
    title: string;
    comment: string;
    rating: number;
    image: string | null;
    submitted_at: string | null;
    rating_categories?: Record<string, number> | null;
}

export interface UseProductReviewsOptions {
    /** Lazy load: scroll ile yorumlar alanı görünür olunca fetch (varsayılan: true) */
    lazyLoad?: boolean;
}

export interface UseProductReviewsReturn {
    reviews: Review[];
    averageRating: number;
    reviewCount: number;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    /** Lazy load için: bu ref'i yorumlar section'ına bağla */
    reviewsTriggerRef: React.RefObject<HTMLElement | null>;
}

const FETCH_TIMEOUT_MS = 30000; // 30 saniye - ağ gecikmeleri için

const reviewsResultCache = new Map<string, Review[]>();

async function fetchReviewsFromApi(productId: number, languageId?: number | null): Promise<Review[]> {
    const cacheKey = `reviews:${productId}:${languageId ?? 'all'}`;
    const cached = reviewsResultCache.get(cacheKey);
    if (cached) return cached;

    return clientFetchDedupe(cacheKey, async () => {
        const fetchOnce = async (includeLanguage: boolean): Promise<Review[]> => {
            const params = new URLSearchParams({
                reviewable_type: 'product',
                reviewable_id: String(productId),
                status: 'approved',
                per_page: '20',
                page: '1',
            });

            if (includeLanguage && languageId) {
                params.set('language_id', String(languageId));
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

            try {
                const response = await fetch(`/api/customer-reviews?${params.toString()}`, {
                    signal: controller.signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(
                        (errorData as { message?: string }).message || `API error: ${response.status}`,
                    );
                }
                const data = await response.json();
                return Array.isArray(data?.data) ? data.data : [];
            } finally {
                clearTimeout(timeoutId);
            }
        };

        let loadedReviews = await fetchOnce(true);
        if (loadedReviews.length === 0 && languageId) {
            loadedReviews = await fetchOnce(false);
        }
        reviewsResultCache.set(cacheKey, loadedReviews);
        return loadedReviews;
    });
}

export function useProductReviews(
    productId: number | null,
    languageId?: number | null,
    enabled: boolean = true,
    options: UseProductReviewsOptions = {}
): UseProductReviewsReturn {
    const { lazyLoad = true } = options;
    const [reviews, setReviews] = useState<Review[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const reviewsTriggerRef = useRef<HTMLElement | null>(null);
    const hasFetchedRef = useRef(false);
    const isPageReadyRef = useRef(false);

    const fetchReviews = useCallback(async () => {
        if (!productId || !enabled) {
            setReviews([]);
            setIsLoading(false);
            return;
        }

        const startTime = performance.now();
        const operationId = `useProductReviews-${Date.now()}`;

        setIsLoading(true);
        setError(null);

        try {
            const loadedReviews = await fetchReviewsFromApi(productId, languageId);
            setReviews(loadedReviews);
        } catch (err) {
            const elapsed = performance.now() - startTime;
            if (err instanceof Error && err.name === 'AbortError') {
                console.error(
                    `[useProductReviews] ${operationId} - TIMEOUT HATASI (${elapsed.toFixed(2)}ms)`,
                    {
                        error: err.message,
                        name: err.name,
                        productId,
                        languageId,
                        timeout: FETCH_TIMEOUT_MS,
                    }
                );
                setError('İstek zaman aşımına uğradı');
            } else {
                console.error(
                    `[useProductReviews] ${operationId} - HATA (${elapsed.toFixed(2)}ms)`,
                    {
                        error: err instanceof Error ? err.message : String(err),
                        name: err instanceof Error ? err.name : 'Unknown',
                        stack: err instanceof Error ? err.stack : undefined,
                        productId,
                        languageId,
                    }
                );
                const errorMessage = err instanceof Error ? err.message : 'Yorumlar yüklenemedi';
                setError(errorMessage);
            }
            setReviews([]);
        } finally {
            setIsLoading(false);
        }
    }, [productId, languageId, enabled]);

    // Sayfa tam yüklendiğinde işaretle
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const onLoad = () => {
            isPageReadyRef.current = true;
        };

        if (document.readyState === 'complete') {
            isPageReadyRef.current = true;
        } else {
            window.addEventListener('load', onLoad);
            return () => window.removeEventListener('load', onLoad);
        }
    }, []);

    // Lazy load: Intersection Observer ile yorumlar alanı görünür olunca fetch
    useEffect(() => {
        if (!enabled || !productId || !lazyLoad) return;

        let cancelled = false;
        let observer: IntersectionObserver | null = null;
        let intervalId: ReturnType<typeof setInterval> | null = null;
        let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

        const tryObserve = () => {
            if (cancelled || !isPageReadyRef.current) return false;
            const el = reviewsTriggerRef.current;
            if (!el) return false;

            observer = new IntersectionObserver(
                (entries) => {
                    if (cancelled) return;
                    const entry = entries[0];
                    if (!entry?.isIntersecting || hasFetchedRef.current) return;

                    hasFetchedRef.current = true;
                    fetchReviews().catch(() => { /* Timeout/AbortError - zaten setError ile gösteriliyor; unhandled rejection önlenir */ });
                    observer?.disconnect();
                },
                {
                    root: null,
                    rootMargin: '200px',
                    threshold: 0.01,
                }
            );
            observer.observe(el);
            return true;
        };

        const run = () => {
            if (isPageReadyRef.current) {
                if (!reviewsTriggerRef.current) {
                    const rafId = requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (!cancelled) tryObserve();
                        });
                    });
                    return () => cancelAnimationFrame(rafId);
                }
                tryObserve();
            } else {
                intervalId = setInterval(() => {
                    if (tryObserve() && intervalId) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                }, 100);
            }
        };

        run();

        // Intersection tetiklenmezse (özellikle karmaşık layout/lazy render durumlarında)
        // yorumları yine de tek sefer fetch ederek "hiç görünmeme" sorununu engelle.
        fallbackTimer = setTimeout(() => {
            if (cancelled || hasFetchedRef.current) return;
            hasFetchedRef.current = true;
            fetchReviews().catch(() => { /* Timeout/AbortError - unhandled rejection önlenir */ });
        }, 4000);

        return () => {
            cancelled = true;
            observer?.disconnect();
            if (intervalId) clearInterval(intervalId);
            if (fallbackTimer) clearTimeout(fallbackTimer);
        };
    }, [productId, languageId, enabled, lazyLoad, fetchReviews]);

    // Lazy load kapalıysa veya enabled değilse: sayfa yüklendikten sonra hemen fetch
    useEffect(() => {
        if (!enabled || !productId || lazyLoad) return;

        const doFetch = () => {
            if (isPageReadyRef.current && !hasFetchedRef.current) {
                hasFetchedRef.current = true;
                fetchReviews().catch(() => { /* Timeout/AbortError - unhandled rejection önlenir */ });
            }
        };

        if (isPageReadyRef.current) {
            doFetch();
        } else {
            const onLoad = () => {
                isPageReadyRef.current = true;
                doFetch();
            };
            if (document.readyState === 'complete') {
                onLoad();
            } else {
                window.addEventListener('load', onLoad);
                return () => window.removeEventListener('load', onLoad);
            }
        }
    }, [productId, languageId, enabled, lazyLoad, fetchReviews]);

    // productId/enabled değişince hasFetched sıfırla
    useEffect(() => {
        hasFetchedRef.current = false;
    }, [productId, languageId, enabled]);

    // Average rating ve review count hesapla
    const averageRating = useMemo(() => {
        if (reviews.length === 0) return 0;
        const sum = reviews.reduce((acc, review) => acc + (review.rating || 0), 0);
        return sum / reviews.length;
    }, [reviews]);

    const reviewCount = reviews.length;

    // refetch: dışarıdan await edildiğinde reject olmamalı (Next.js Timeout overlay önleme)
    const refetch = useCallback(() => {
        return fetchReviews().catch(() => {});
    }, [fetchReviews]);

    return {
        reviews,
        averageRating,
        reviewCount,
        isLoading,
        error,
        refetch,
        reviewsTriggerRef,
    };
}
